import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useMemo } from "react"


import { AvatarModel } from "@/components/Avatar/AvatarModel.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { WithImage } from "@/components/WithImage/WithImage.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { getFullName } from "@/hooks/useProfile.ts"

import type { TD_MemberProfile, TD_MemberProfile_Minimal } from "@/lib/client/generated/TypeDefinitions.ts"


const cuteGradients = [
  { from: "#B5FFC1", to: "#17E6E8" },
  { from: "#FFF73A", to: "#17E6E8" },
  { from: "#05FF00", to: "#F4F859" },
  { from: "#167DFF", to: "#85FFE9" },
  { from: "#5FFDFF", to: "#51CCFF" },
  { from: "#1347FF", to: "#0E032F" },
  { from: "#0B0D3B", to: "#7F52FF" },
  { from: "#FF85A2", to: "#FFD15C" }
]

export const Avatar = observer((props: {
  profile: TD_MemberProfile_Minimal | TD_MemberProfile
  size: number
}) => {
  const model = useModel(() => new AvatarModel(props.profile), [props.profile])
  const fullName = getFullName(props.profile)

  return (
    <AvatarWrapper aria-label={fullName} size={props.size}>
      {model.hasAvatarPicture
        ? (
          <AvatarImage
            src={`/d/${props.profile.avatar}`}
            key={props.profile.avatar}
            alt="User avatar"
            size={props.size}
            loading="lazy"
            draggable={false}
            onError={model.onImageError}
          />
        )
        : <CuteStub size={props.size} profile={props.profile} />
      }
    </AvatarWrapper>
  )
})

const CuteStub = (props: {
  profile: TD_MemberProfile_Minimal
  size: number
}) => {
  const stub = useMemo(() => {
    const avatarCode = ((code(props.profile.username) % 17) + 1).toString().padStart(2, "0")
    const path = `@/avatars/character_${avatarCode}.svg`

    return {
      image: import(`@/avatars/character_${avatarCode}.svg?inline`).then(module => module.default as string),
      path: path,
      gradient: cuteGradients[code(props.profile.username) % cuteGradients.length]
    }
  }, [props.profile.username])

  return (
    <WithImage image={stub.image} path={stub.path} width={props.size} height={props.size}>
      {image => <AvatarStub
        size={props.size}
        image={image}
        gradientFrom={stub.gradient.from}
        gradientTo={stub.gradient.to} />
      }
    </WithImage>
  )
}

function code(str: string): number {
  return Math.abs(
    str.split("").reduce((acc, char) => acc * 31 + char.charCodeAt(0), 0)
  )
}

export const AvatarWrapper = styled(Vertical)<{
  size: number
}>`
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;

  position: relative;
  overflow: hidden;

  text-align: center;
  user-select: none;

  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};

  border-radius: var(--border-radius-round);
  background-color: var(--text-color-10);
`

const AvatarImage = styled.img<{
  size: number
}>`
  border-radius: var(--border-radius-round);

  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};
`

export const AvatarStub = styled(Horizontal)<{
  size: number
  image?: string
  gradientFrom?: string
  gradientTo?: string
}>`
  width: ${props => `${props.size}px`};
  height: ${props => `${props.size}px`};

  background: ${props => props.image && props.gradientFrom && props.gradientTo ? `no-repeat url("${props.image}") center 5.7% / 113%, linear-gradient(to bottom, ${props.gradientFrom}, ${props.gradientTo})` : "none"};

  border-radius: var(--border-radius-round);
`

export const PrincipalStubAvatar = styled.div<{ size: number }>`
  background: var(--text-color-10);
  border-radius: 100%;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
`
